import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { useAuth } from "../context/AuthContext";
import { Plus, PencilSimple, Trash, X, Buildings } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Branches() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [branches, setBranches] = useState([]);
  const [counts, setCounts] = useState({});
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");

  const load = async () => {
    const [b, a] = await Promise.all([api.get("/branches"), api.get("/analytics/overview")]);
    setBranches(b.data);
    setCounts(a.data.by_branch || {});
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setName(""); setEditing("new"); };
  const openEdit = (br) => { setName(br.name); setEditing(br); };
  const close = () => setEditing(null);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing === "new") {
        await api.post("/branches", { name });
        toast.success("Branch added");
      } else {
        await api.patch(`/branches/${editing.id}`, { name });
        toast.success("Branch updated");
      }
      close(); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (br) => {
    if (!window.confirm(`Delete branch "${br.name}"? Existing leads will keep referencing it.`)) return;
    try {
      await api.delete(`/branches/${br.id}`);
      toast.success("Branch removed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="min-h-screen">
      <PageHeader
        eyebrow="Network"
        title="Branches"
        description="All physical units across the network. Lead counts shown live."
        actions={isAdmin && (
          <button onClick={openNew} data-testid="add-branch-button"
            className="px-4 py-2.5 text-sm font-semibold bg-primary text-primary-foreground hover:bg-[#002277] rounded-sm flex items-center gap-2">
            <Plus size={16} weight="bold" /> Add branch
          </button>
        )}
      />

      <div className="px-8 py-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {branches.map((b) => (
          <div key={b.id} className="border border-border bg-white p-5 group hover:-translate-y-px transition-transform" data-testid={`branch-card-${b.id}`}>
            <div className="flex items-start justify-between">
              <div className="w-8 h-8 bg-secondary flex items-center justify-center">
                <Buildings size={16} />
              </div>
              {isAdmin && (
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => openEdit(b)} data-testid={`edit-branch-${b.id}`} className="p-1.5 hover:bg-secondary border border-transparent hover:border-border">
                    <PencilSimple size={14} />
                  </button>
                  <button onClick={() => remove(b)} data-testid={`delete-branch-${b.id}`} className="p-1.5 text-destructive hover:bg-destructive/10 border border-transparent hover:border-destructive/40">
                    <Trash size={14} />
                  </button>
                </div>
              )}
            </div>
            <div className="font-display text-xl font-bold tracking-tight mt-4">{b.name}</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">Branch unit</div>
            <div className="border-t border-border mt-4 pt-3 flex items-baseline justify-between">
              <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Leads</span>
              <span className="font-display text-2xl font-bold tabular-nums">{counts[b.id] || 0}</span>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={close}>
          <div className="bg-white border border-border w-full max-w-md fade-up" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-border px-6 py-4">
              <h2 className="font-display text-2xl font-bold tracking-tight">
                {editing === "new" ? "New branch" : "Edit branch"}
              </h2>
              <button onClick={close} className="p-1.5 hover:bg-secondary"><X size={16} /></button>
            </div>
            <form onSubmit={save} className="p-6 space-y-4">
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-foreground/70 block mb-1.5">Branch name</span>
                <input required value={name} onChange={(e) => setName(e.target.value)}
                  data-testid="branch-form-name"
                  className="w-full px-3 py-2 border border-border rounded-sm bg-white text-sm focus:border-primary focus:outline-none" />
              </label>
              <div className="flex justify-end gap-2 pt-2 border-t border-border">
                <button type="button" onClick={close} className="px-4 py-2 text-sm font-semibold border border-border hover:bg-secondary rounded-sm">Cancel</button>
                <button type="submit" data-testid="branch-form-submit" className="px-4 py-2 text-sm font-semibold bg-primary text-primary-foreground hover:bg-[#002277] rounded-sm">
                  {editing === "new" ? "Create" : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
